import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { profileService } from '../services/profileService';

interface TickerLinkState {
  linkedTicker: string | null;
  linkedWindowIds: Set<string>;
  autoFocusEnabled: boolean;
  isInitialized: boolean;
}

interface TickerLinkContextValue extends Omit<TickerLinkState, 'linkedWindowIds'> {
  linkedWindowIds: string[];
  setLinkedTicker: (ticker: string) => void;
  toggleWindowLink: (windowId: string) => void;
  isWindowLinked: (windowId: string) => boolean;
  setAutoFocusEnabled: (enabled: boolean) => void;
  globalInputRef: React.RefObject<HTMLInputElement | null>;
  skipNextClear: React.MutableRefObject<boolean>;
}

const TickerLinkContext = createContext<TickerLinkContextValue | null>(null);

const defaultState: TickerLinkState = {
  linkedTicker: null,
  linkedWindowIds: new Set(),
  autoFocusEnabled: false,
  isInitialized: false,
};

// Debounce timeout for saving state
let saveTimeout: ReturnType<typeof setTimeout> | null = null;

export function TickerLinkProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<TickerLinkState>(defaultState);
  const globalInputRef = useRef<HTMLInputElement | null>(null);
  const skipNextClear = useRef<boolean>(false);

  // Load persisted state from backend
  const { data: frontendState } = useQuery({
    queryKey: ['frontendState'],
    queryFn: () => profileService.getFrontendState(),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

  // Initialize from persisted state
  useEffect(() => {
    if (frontendState?.frontend_state?.tickerLinkState && !state.isInitialized) {
      const saved = frontendState.frontend_state.tickerLinkState;
      setState({
        linkedTicker: saved.linkedTicker || null,
        linkedWindowIds: new Set(saved.linkedWindowIds || []),
        autoFocusEnabled: saved.autoFocusEnabled ?? false,
        isInitialized: true,
      });
    } else if (frontendState && !state.isInitialized) {
      setState((prev) => ({ ...prev, isInitialized: true }));
    }
  }, [frontendState, state.isInitialized]);

  // Persist state changes to backend (debounced)
  const persistState = useCallback((newState: TickerLinkState) => {
    if (saveTimeout) {
      clearTimeout(saveTimeout);
    }
    saveTimeout = setTimeout(() => {
      profileService.saveFrontendState({
        tickerLinkState: {
          linkedTicker: newState.linkedTicker,
          linkedWindowIds: Array.from(newState.linkedWindowIds),
          autoFocusEnabled: newState.autoFocusEnabled,
        },
      });
    }, 1000);
  }, []);

  const setLinkedTicker = useCallback((ticker: string) => {
    setState((prev) => {
      const newState = { ...prev, linkedTicker: ticker.toUpperCase() };
      persistState(newState);
      return newState;
    });
  }, [persistState]);

  const toggleWindowLink = useCallback((windowId: string) => {
    setState((prev) => {
      const newLinkedWindowIds = new Set(prev.linkedWindowIds);
      if (newLinkedWindowIds.has(windowId)) {
        newLinkedWindowIds.delete(windowId);
      } else {
        newLinkedWindowIds.add(windowId);
      }
      const newState = { ...prev, linkedWindowIds: newLinkedWindowIds };
      persistState(newState);
      return newState;
    });
  }, [persistState]);

  const isWindowLinked = useCallback(
    (windowId: string) => state.linkedWindowIds.has(windowId),
    [state.linkedWindowIds]
  );

  const setAutoFocusEnabled = useCallback((enabled: boolean) => {
    setState((prev) => {
      const newState = { ...prev, autoFocusEnabled: enabled };
      persistState(newState);
      return newState;
    });
  }, [persistState]);

  // Global keyboard shortcut: typing anywhere focuses the ticker input
  useEffect(() => {
    if (!state.autoFocusEnabled) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't intercept if user is already in an input, textarea, or contenteditable
      const target = e.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      // Don't intercept modifier keys or special keys
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      // Only intercept alphanumeric keys
      if (/^[a-zA-Z0-9]$/.test(e.key)) {
        if (globalInputRef.current) {
          // Set flag to prevent onFocus from clearing the input
          skipNextClear.current = true;
          // Focus the input - the keypress will naturally flow into it
          globalInputRef.current.focus();
          globalInputRef.current.select();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [state.autoFocusEnabled]);

  return (
    <TickerLinkContext.Provider
      value={{
        linkedTicker: state.linkedTicker,
        linkedWindowIds: Array.from(state.linkedWindowIds),
        autoFocusEnabled: state.autoFocusEnabled,
        isInitialized: state.isInitialized,
        setLinkedTicker,
        toggleWindowLink,
        isWindowLinked,
        setAutoFocusEnabled,
        globalInputRef,
        skipNextClear,
      }}
    >
      {children}
    </TickerLinkContext.Provider>
  );
}

export function useTickerLink() {
  const context = useContext(TickerLinkContext);
  if (!context) {
    throw new Error('useTickerLink must be used within a TickerLinkProvider');
  }
  return context;
}
